import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { SafeLabsCTA } from '../components/SafeLabsCTA';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DemoPage = () => {
  const [formData, setFormData] = useState({ full_name: '', email: '', organization: '', role: '', reason: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [showDemo, setShowDemo] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/demo/request-access`, formData);
      if (response.data.success) {
        setSubmitted(true);
        toast.success('Request Submitted', { description: response.data.message });
      }
    } catch (error) {
      toast.error('Submission failed. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleAccessToken = async (e) => {
    e.preventDefault();
    if (!accessToken.trim()) return;
    try {
      const response = await axios.get(`${API}/demo/verify/${accessToken}`);
      if (response.status === 200) {
        setShowDemo(true);
      }
    } catch (error) {
      toast.error('Invalid or expired access token.');
    }
  };

  if (showDemo) {
    return (
      <div data-testid="demo-page" className="w-full h-screen">
        <iframe
          src={`${API}/demo/verify/${accessToken}`}
          className="w-full h-full border-0"
          title="SAIL MVP Demo"
        />
      </div>
    );
  }

  return (
    <div data-testid="demo-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
              SAIL MVP Demo
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              A working demonstration of the SAIL implementation layer.
            </p>
            <p className="text-lg md:text-xl text-slate-300">
              Access is granted upon review.
            </p>
          </div>
        </div>
      </section>

      {/* About the Demo */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">About This Demo</p>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-slate-700">
                The SAIL MVP is a safety intervention layer designed to operate on-device or through browser extension.
              </p>
              <p className="text-lg text-slate-700">
                It demonstrates how language escalation is detected and how safety mechanisms are triggered — for both predatory behaviour and innocent communications between peers.
              </p>
            </div>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Test as a child or as a predator</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">18 pre-set conversation scenarios and triggers</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Real-time threat level detection and intervention tiers</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Covers grooming, sextortion, platform migration, and peer boundary testing</p>
              </div>
            </div>
            <p className="text-lg text-slate-900 font-semibold">
              For demonstration purposes only.
            </p>
          </div>
        </div>
      </section>

      {/* Access Form / Token Entry */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Request Access */}
            <div>
              <p className="caption text-cyan-700 mb-4">Request Access</p>
              {submitted ? (
                <div className="bg-slate-50 border border-slate-200 p-8">
                  <p className="text-lg text-slate-900 font-semibold mb-4">Request submitted.</p>
                  <p className="text-slate-700">You will receive an access token upon approval. Use it in the panel to the right to access the demo.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-request-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900"
                        data-testid="demo-input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900"
                        data-testid="demo-input-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Organization</label>
                      <input
                        type="text"
                        value={formData.organization}
                        onChange={(e) => setFormData({...formData, organization: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Role</label>
                      <input
                        type="text"
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Reason for Access</label>
                    <textarea
                      value={formData.reason}
                      onChange={(e) => setFormData({...formData, reason: e.target.value})}
                      className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 h-24 resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full"
                    data-testid="demo-btn-submit"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Access'}
                  </button>
                </form>
              )}
            </div>

            {/* Enter Access Token */}
            <div>
              <p className="caption text-slate-500 mb-4">Have an Access Token?</p>
              <div className="bg-slate-50 border border-slate-200 p-8">
                <p className="text-sm text-slate-600 mb-6">
                  If your access has been approved, enter your token below to launch the demo.
                </p>
                <form onSubmit={handleAccessToken} className="space-y-4">
                  <input
                    type="text"
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    placeholder="Enter access token"
                    className="w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900"
                    data-testid="demo-input-token"
                  />
                  <button
                    type="submit"
                    className="btn-primary w-full"
                    data-testid="demo-btn-access"
                  >
                    Access Demo
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      <SafeLabsCTA />
    </div>
  );
};

export default DemoPage;
